/*! Copyright (c) PureOilStudio. */

class ProductCountdownSync extends HTMLElement {
  #container;
  #documentObserver;
  #sectionLoadHandler;
  #source;
  #sourceCompleteHandler;
  #sourceObserver;
  #timer;

  constructor() {
    super();
    this.#sectionLoadHandler = () => this.#connect();
    this.#sourceCompleteHandler = () => this.#hideContainer();
  }

  connectedCallback() {
    this.#container = this.closest("[data-countdown-sync-container]") || this;
    this.#timer = this.querySelector("countdown-timer");
    this.#hideContainer();
    document.addEventListener("shopify:section:load", this.#sectionLoadHandler);

    customElements.whenDefined("countdown-timer").then(() => {
      if (this.isConnected) this.#connect();
    });
  }

  disconnectedCallback() {
    document.removeEventListener("shopify:section:load", this.#sectionLoadHandler);
    this.#disconnectSource();
    this.#disconnectDocumentObserver();
  }

  #connect() {
    this.#disconnectSource();
    this.#disconnectDocumentObserver();

    const sourceKey = this.dataset.countdownSourceKey;
    if (!sourceKey || !this.#timer) return;

    this.#source = Array.from(document.querySelectorAll("countdown-timer[data-countdown-source]")).find(
      (timer) => timer !== this.#timer && timer.dataset.countdownSource === sourceKey,
    );

    if (!this.#source) {
      this.#hideContainer();
      this.#watchForSource();
      return;
    }

    this.#source.addEventListener("on:countdown:completed", this.#sourceCompleteHandler);
    this.#sourceObserver = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.type === "attributes" && mutation.target === this.#source)) {
        this.#applySourceClock();
      }
      this.#mirrorSource();
    });

    const sourceContainer = this.#source.closest(".utility-bar__item--countdown") || this.#source;
    this.#sourceObserver.observe(sourceContainer, {
      attributes: true,
      attributeFilter: ["data-cycle-seconds", "data-is-default", "data-server-timestamp", "data-target-timestamp", "hidden"],
      characterData: true,
      childList: true,
      subtree: true,
    });

    this.#applySourceClock();
    this.#mirrorSource();
  }

  #applySourceClock() {
    if (!this.#source || !this.#timer) return;

    ["data-target-timestamp", "data-server-timestamp", "data-cycle-seconds", "data-is-default"].forEach((attribute) => {
      const value = this.#source.getAttribute(attribute);
      if (value === null) {
        this.#timer.removeAttribute(attribute);
      } else {
        this.#timer.setAttribute(attribute, value);
      }
    });

    if (this.#source.targetDate instanceof Date) {
      this.#timer.targetDate = new Date(this.#source.targetDate.getTime());
      this.#timer.serverTimestamp = this.#source.serverTimestamp;
      this.#timer.clockStart = this.#source.clockStart;
      this.#timer.startCountdown();
    }
  }

  #mirrorSource() {
    if (!this.#source || !this.#timer) return;

    const sourceContainer = this.#source.closest(".utility-bar__item--countdown");
    if (sourceContainer?.hasAttribute("hidden")) {
      this.#hideContainer();
      return;
    }

    ["days", "hours", "minutes", "seconds"].forEach((unit) => {
      const sourceUnit = this.#source.querySelector(`.countdown__unit[data-unit="${unit}"]`);
      const targetUnit = this.#timer.querySelector(`.countdown__unit[data-unit="${unit}"]`);
      const sourceValue = sourceUnit?.querySelector(".countdown__value");
      const targetValue = targetUnit?.querySelector(".countdown__value");

      if (sourceValue && targetValue) targetValue.textContent = sourceValue.textContent;
      if (sourceUnit && targetUnit) {
        const keepDaysVisible = unit === "days" && this.#timer.dataset.showZeroDays === "true";
        targetUnit.toggleAttribute("hidden", !keepDaysVisible && sourceUnit.hasAttribute("hidden"));
      }
    });

    const sourceDividers = this.#source.querySelectorAll(".countdown__divider");
    const targetDividers = this.#timer.querySelectorAll(".countdown__divider");
    targetDividers.forEach((divider, index) => {
      divider.toggleAttribute("hidden", sourceDividers[index]?.hasAttribute("hidden") || false);
    });

    const sourceLabel = this.#source.getAttribute("aria-label");
    if (sourceLabel) this.#timer.setAttribute("aria-label", sourceLabel);
    this.#showContainer();
  }

  #watchForSource() {
    if (this.#documentObserver) return;

    this.#documentObserver = new MutationObserver(() => {
      const sourceKey = this.dataset.countdownSourceKey;
      const sourceExists = Array.from(document.querySelectorAll("countdown-timer[data-countdown-source]")).some(
        (timer) => timer.dataset.countdownSource === sourceKey,
      );
      if (sourceExists) this.#connect();
    });
    this.#documentObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  #disconnectSource() {
    if (this.#source) this.#source.removeEventListener("on:countdown:completed", this.#sourceCompleteHandler);
    if (this.#sourceObserver) this.#sourceObserver.disconnect();
    this.#source = undefined;
    this.#sourceObserver = undefined;
  }

  #disconnectDocumentObserver() {
    if (this.#documentObserver) this.#documentObserver.disconnect();
    this.#documentObserver = undefined;
  }

  #hideContainer() {
    if (this.#container) this.#container.setAttribute("hidden", "");
  }

  #showContainer() {
    if (this.#container) this.#container.removeAttribute("hidden");
  }
}

if (!customElements.get("product-countdown-sync")) {
  customElements.define("product-countdown-sync", ProductCountdownSync);
}
